import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as api from "./api";

const key = (
  search: string,
  invitation: api.InvitationStatus,
  active: api.ActiveFilter,
) => ["portal", "members", search, invitation, active] as const;
export function useMembers(
  search: string,
  invitation: api.InvitationStatus,
  active: api.ActiveFilter,
) {
  return useInfiniteQuery({
    queryKey: key(search, invitation, active),
    queryFn: ({ pageParam }) =>
      api.listMembers({
        search,
        invitationStatus: invitation,
        active,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.meta.next ?? undefined,
  });
}
export const useMember = (id: string | null) =>
  useQuery({
    queryKey: ["portal", "member", id],
    queryFn: () => api.getMember(id!),
    enabled: Boolean(id),
  });
// CONFLICT-01 (ola 3, resolución humana): el contador de la lista solo puede
// mostrar el patrón «X de Y resultados» del prototipo (admin.jsx:777) si
// conoce el total sin filtrar además del filtrado. `useMembers` expone
// `meta.total` como `count(where <filtros>)` (member.repository.ts:168), que
// es siempre el total FILTRADO — nunca el universo completo —, porque los
// filtros viajan como query params al servidor en vez de aplicarse en
// cliente como hace el prototipo sobre su arreglo fijo. No hay endpoint que
// devuelva ambos números en una sola respuesta, así que este hook pide la
// misma lista sin ningún filtro para leer únicamente su `meta.total`; React
// Query lo cachea bajo una clave propia, separada del listado filtrado.
export const useMembersOverallTotal = () =>
  useQuery({
    queryKey: ["portal", "members-overall-total"],
    queryFn: () =>
      api.listMembers({ search: "", invitationStatus: "all", active: "all" }),
    select: (page) => page.meta.total,
  });
export const useMemberCatalog = (
  catalog: "id-types" | "cities" | "workshops" | "inactivation-reasons",
) =>
  useQuery({
    queryKey: ["portal", "catalog-entries", catalog],
    queryFn: () => api.listMemberCatalog(catalog),
  });
export const useAdvisors = () =>
  useQuery({
    queryKey: ["portal", "member-advisors"],
    queryFn: api.listAdvisors,
  });
export function useMemberMutations() {
  const client = useQueryClient();
  const invalidate = () => {
    client.invalidateQueries({ queryKey: ["portal", "members"] });
    // TanStack Query hace coincidencia por elemento exacto del arreglo, no
    // por prefijo de string: `["portal", "members-overall-total"]` no
    // coincide con `["portal", "members"]` de arriba, así que crear,
    // inactivar o reactivar un miembro cambia el universo sin filtros y
    // necesita su propia invalidación explícita.
    client.invalidateQueries({ queryKey: ["portal", "members-overall-total"] });
  };
  return {
    create: useMutation({
      mutationFn: api.createMember,
      onSuccess: invalidate,
    }),
    edit: useMutation({
      mutationFn: ({
        id,
        input,
      }: {
        id: string;
        input: api.MemberUpdateInput;
      }) => api.editMember(id, input),
      onSuccess: invalidate,
    }),
    invite: useMutation({
      mutationFn: api.inviteMember,
      onSuccess: invalidate,
    }),
    batchInvite: useMutation({
      mutationFn: api.inviteMembersBatch,
      onSuccess: invalidate,
    }),
    inactivate: useMutation({
      mutationFn: ({
        id,
        input,
        idempotencyKey,
      }: {
        id: string;
        input: {
          inactivationReasonId: string;
          detail?: string;
          confirmed: boolean;
        };
        idempotencyKey: string;
      }) => api.inactivateMember(id, input, idempotencyKey),
      onSuccess: invalidate,
    }),
    reactivate: useMutation({
      mutationFn: ({
        id,
        input,
      }: {
        id: string;
        input: { purchaseDate: string; reason: string };
      }) => api.reactivateMember(id, input),
      onSuccess: invalidate,
    }),
  };
}
